import type {
  AttachmentStatus,
  ChatIdentityKind,
  ChatPagination,
  MessageReference,
  RecipientKind,
  ThreadIdParams,
  TopicColor,
} from './types';

export type {
  CreateThreadBody,
  CreateThreadType,
  MessageReference,
  SendMessageBody,
  TopicColor,
} from './types';

// Contexts

export const CONTEXT_KINDS = ['contact', 'topic', 'broadcast', 'groupChat'] as const;
export type ContextKind = (typeof CONTEXT_KINDS)[number];

// Subject broadcasts

export const BROADCAST_SUBJECT_TYPES = ['Fund', 'Company', 'Event'] as const;
export type BroadcastSubjectType = (typeof BROADCAST_SUBJECT_TYPES)[number];

/** The Fund, Company or Event whose system-fed thread a context or thread row belongs to. */
export interface BroadcastSubject {
  type: BroadcastSubjectType;
  id: string;
}

export const MEMBER_KINDS = ['follower', 'investor', 'ticket_holder'] as const;
export type MemberKind = (typeof MEMBER_KINDS)[number];

export const CONTEXT_KIND_FILTERS = ['all', ...CONTEXT_KINDS] as const;
export type ContextKindFilter = (typeof CONTEXT_KIND_FILTERS)[number];

export const CONTEXT_STATES = [
  'needsAction',
  'unread',
  'awaitingReply',
  'waiting',
  'muted',
] as const;
export type ContextState = (typeof CONTEXT_STATES)[number];

export interface ContextIdParams {
  /**
   * Opaque id from `GET /contexts`: `contact:<ParticipantType>:<uuid>`, `topic:<uuid>`,
   * `broadcast:<Fund|Company|Event>:<uuid>` or `groupChat:<threadUuid>`.
   */
  contextId: string;
}

export interface ContextRow {
  id: string;
  kind: ContextKind;
  name: string;
  /** Email-only for a contact; null for topic, broadcast and group-chat contexts. */
  subtitle: string | null;
  avatarUrl: string | null;
  /** Topic contexts only. */
  color: TopicColor | null;
  /** `0` for a counterparty nobody has written to yet. */
  threadCount: number;
  unreadCount: number;
  lastActivityAt: string | null;
  /** Broadcast rows only. */
  subject?: BroadcastSubject | null;
  memberKinds?: MemberKind[];
  /** A desk topic the caller is not granted: listed by name only, with nothing counted. */
  locked?: boolean;
}

export interface ContextKindUnread {
  all: number;
  contact: number;
  topic: number;
  broadcast: number;
  groupChat: number;
}

export interface ContextStateCounts {
  needsAction: number;
  unread: number;
  awaitingReply: number;
  waiting: number;
  muted: number;
}

export interface GetContextsParams {
  categoryNodeIds?: string[];
  topicIds?: string[];
  search?: string;
  kind?: ContextKindFilter;
  state?: ContextState;
  /**
   * 1–100; a larger value is rejected with 400.
   * @default 50
   */
  limit?: number;
  offset?: number;
}

export interface GetContextsRes {
  rows: ContextRow[];
  kindsPresent: ContextKind[];
  kindUnread: ContextKindUnread;
  stateCounts: ContextStateCounts;
  /**
   * Contexts in the whole directory before `search`/`kind`/`state` narrow `rows`, so an empty
   * directory is `0` while a filter that matched nothing keeps its real total. A brand-new
   * account is `1`: the Gamma desk is always reachable.
   */
  total: number;
  pagination: ChatPagination;
}

export type ContextListResponse = GetContextsRes;

export interface ContextThreadTopic {
  id: string;
  label: string;
  color: TopicColor;
  isSystem: boolean;
  /** Not sent today: a thread the caller can see never carries a topic locked for them. */
  locked?: boolean;
}

export interface ContextThreadRow {
  id: string;
  name: string;
  preview: string | null;
  lastActivityAt: string;
  unreadCount: number;
  isBroadcast: boolean;
  isOwnBroadcast: boolean;
  isArchived: boolean;
  isResolved: boolean;
  isMuted: boolean;
  isPrivate: boolean;
  isWaiting: boolean;
  ownIdentity: ChatIdentityKind;
  awaitingMyReply: boolean;
  attachmentCount: number;
  topics: ContextThreadTopic[];
  filedContextIds: string[];
  parentThreadId: string | null;
  /** The object this thread follows; `null` for every conversation. */
  subject: BroadcastSubject | null;
}

export interface GetContextThreadsParams extends ContextIdParams {
  /** Resolving archives every row, so a resolved thread also needs `includeArchived`. */
  includeResolved?: boolean;
  includeArchived?: boolean;
  /** 1–100 @default 50 */
  limit?: number;
  offset?: number;
}

export interface GetContextThreadsRes {
  rows: ContextThreadRow[];
  pagination: ChatPagination;
}

export type ContextThreadsResponse = GetContextThreadsRes;

// Merged feed

export const FEED_ENTRY_KINDS = ['message', 'event'] as const;
export type FeedEntryKind = (typeof FEED_ENTRY_KINDS)[number];

export interface FeedSender {
  id: string;
  name: string;
  subtitle: string | null;
  avatarUrl: string | null;
  kind: ChatIdentityKind;
}

export interface FeedAttachment {
  fileId: string;
  filename: string;
  url: string;
  size: number;
  status: AttachmentStatus;
  contentType: string;
  urlExpiresAt: string;
}

export interface FeedMessageEntry {
  kind: 'message';
  id: string;
  threadId: string;
  threadName: string;
  sentAt: string;
  sender: FeedSender;
  body: string | null;
  attachments: FeedAttachment[];
  isUnread: boolean;
  isOwn: boolean;
  isBroadcast: boolean;
  isMuted: boolean;
  isEdited: boolean;
  editedAt: string | null;
  /** Deep link of a broadcast entry to the object it announces; `null` for a typed message. */
  link: string | null;
  additional?: { originalSenderId: string };
  replyTo?: MessageReference;
  forwardedFrom?: MessageReference;
}

export const THREAD_EVENT_TYPES = [
  'renamed',
  'resolved',
  'reopened',
  'markedPrivate',
  'madeVisible',
  'forkedFromBroadcast',
  'participantsAdded',
  'participantRoleChanged',
  'participantLeft',
] as const;
export type ThreadEventType = (typeof THREAD_EVENT_TYPES)[number];

export type ThreadEventPayload = Record<string, string | number | boolean | null | string[]>;

export interface ThreadEventActor {
  userId: string | null;
  displayName: string | null;
}

export interface FeedEventEntry {
  kind: 'event';
  id: string;
  threadId: string;
  threadName: string;
  type: ThreadEventType;
  actor: ThreadEventActor;
  occurredAt: string;
  payload: ThreadEventPayload;
}

export type FeedEntry = FeedMessageEntry | FeedEventEntry;

export interface GetContextMessagesParams extends ContextIdParams {
  threadId?: string;
  /** Opaque `base64url` cursor from the previous page's `nextCursor`. */
  before?: string;
  /** 1–100 @default 50 */
  limit?: number;
}

export interface GetContextMessagesRes {
  /** Oldest first within the page; pages themselves walk backwards through `nextCursor`. */
  entries: FeedEntry[];
  nextCursor: string | null;
  hasMore: boolean;
}

export type ContextFeedResponse = GetContextMessagesRes;

export type GetContextMessagesQueryParams = Omit<GetContextMessagesParams, 'before'>;

export interface MarkContextReadVars extends ContextIdParams {
  /** Normally the `sentAt` of the newest rendered entry; absent reads the whole context. */
  upTo?: string;
}

export const CONTEXT_RELATIONSHIPS = [
  'Individual',
  'Entity',
  'Direct connection',
  'Contacted you',
  'Gamma team',
] as const;
export type ContextRelationship = (typeof CONTEXT_RELATIONSHIPS)[number];

export interface ContextSummary {
  id: string;
  kind: ContextKind;
  name: string;
  subtitle: string | null;
  avatarUrl: string | null;
  relationships: ContextRelationship[];
  /** The object a broadcast context follows; `null` for every other kind. */
  subject: BroadcastSubject | null;
  /** Why the viewer is a member of that subject thread; empty elsewhere and for its owner. */
  memberKinds: MemberKind[];
  /** Always `null` this round. */
  coInvestorNote: string | null;
}

// Topics

export const TOPIC_LABEL_MAX_LENGTH = 40;
export const THREAD_TOPICS_MAX = 20;

export interface Topic {
  id: string;
  label: string;
  color: TopicColor;
  isSystem: boolean;
  archivedAt: string | null;
  /** The caller's roles are not granted this desk topic: it opens nothing and takes no writes. */
  locked: boolean;
  /** Sent to a roles admin of the workspace only: the roles granted this desk topic. */
  visibleToRoleIds?: string[];
}

export interface GetTopicsRes {
  topics: Topic[];
}

export type TopicsResponse = GetTopicsRes;

export interface CreateTopicBody {
  label: string;
  color: TopicColor;
  /** Desk topics only; left out, the server grants the creator's own roles on the desk's plane. */
  visibleToRoleIds?: string[];
}

export interface UpdateTopicVars {
  topicId: string;
  label?: string;
  color?: TopicColor;
}

export interface TopicIdParams {
  topicId: string;
}

export interface SetTopicVisibilityVars extends TopicIdParams {
  /** The roles granted the topic, replacing the current ones; `[]` leaves it to full-view roles. */
  roleIds: string[];
}

export interface SetThreadTopicsVars extends ThreadIdParams {
  /** Replace-set of live topic ids owned by the acting identity; at most 20. */
  topicIds: string[];
}

// Filings

export const THREAD_FILINGS_MAX = 20;

export interface SetThreadFilingsVars extends ThreadIdParams {
  /** Replace-set of contact context ids; at most 20. Own counterparties are dropped server-side. */
  contextIds: string[];
}

// Thread commands

export const THREAD_NAME_MAX_LENGTH = 255;

export interface RenameThreadVars extends ThreadIdParams {
  name: string;
}

// Participants

export const THREAD_PARTICIPANT_ROLES = ['owner', 'admin', 'member'] as const;
export type ThreadParticipantRole = (typeof THREAD_PARTICIPANT_ROLES)[number];

export const ASSIGNABLE_PARTICIPANT_ROLES = ['admin', 'member'] as const;
export type AssignableParticipantRole = Extract<
  ThreadParticipantRole,
  (typeof ASSIGNABLE_PARTICIPANT_ROLES)[number]
>;

export interface ThreadParticipantRow {
  participantId: string;
  isStarter: boolean;
  role: ThreadParticipantRole;
  kind: ChatIdentityKind;
  name: string | null;
  avatarUrl: string | null;
  addedAt: string;
}

export interface GetThreadParticipantsParams extends ThreadIdParams {
  /** 1–200 @default 50 */
  limit?: number;
  offset?: number;
}

export interface GetThreadParticipantsRes {
  rows: ThreadParticipantRow[];
  canManageParticipants: boolean;
  canManageAdmins: boolean;
  canLeave: boolean;
  pagination: ChatPagination;
}

export type ThreadParticipantsResponse = GetThreadParticipantsRes;

export const ADD_PARTICIPANT_KINDS = ['user', 'entity'] as const;
export type AddParticipantKind = Extract<RecipientKind, (typeof ADD_PARTICIPANT_KINDS)[number]>;

export const THREAD_PARTICIPANTS_ADD_MAX = 50;

export interface AddThreadParticipantsVars extends ThreadIdParams {
  /** 1–50 entries; anyone already in the thread is ignored. */
  recipients: { id: string; type: AddParticipantKind; role?: AssignableParticipantRole }[];
}

export interface RemoveThreadParticipantVars extends ThreadIdParams {
  participantId: string;
  /** Which membership of this identity to remove — one id can hold several rows of a thread. */
  kind: RecipientKind;
}

export interface SetThreadParticipantRoleVars extends ThreadIdParams {
  participantId: string;
  kind: RecipientKind;
  role: AssignableParticipantRole;
}

export type LeaveThreadVars = ThreadIdParams;

export const RECIPIENT_KIND_BY_IDENTITY_KIND: Record<ChatIdentityKind, RecipientKind> = {
  individual: 'user',
  entityDesk: 'entity',
  gammaDesk: 'gamma',
};

export interface EntityBadgeCount {
  entityId: string;
  count: number;
  displayName?: string;
}

export interface BadgeCount {
  total: number;
  user: number;
  entities: EntityBadgeCount[];
  gamma: number;
}

// WebSocket payloads (D25)

export interface ThreadStatePatch {
  isMuted?: boolean;
  isPrivate?: boolean;
  isResolved?: boolean;
  isArchived?: boolean;
  isWaiting?: boolean;
}

export interface ThreadStateChangedData extends ThreadIdParams {
  state: ThreadStatePatch;
  actorUserId: string;
  occurredAt: string;
}

/** Reaches the owning identity's room only — topics are private to the identity that filed them. */
export interface ThreadTopicsChangedData extends ThreadIdParams {
  topics: ContextThreadTopic[];
}

export interface ThreadEventCreatedData extends ThreadIdParams {
  event: {
    id: string;
    type: ThreadEventType;
    actor: ThreadEventActor;
    occurredAt: string;
    payload: ThreadEventPayload;
  };
}
